import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { ArrowLeft, Factory, MapPin, DollarSign, AlertTriangle, Trash2, Bell, BellOff, ExternalLink, Shield, ShieldCheck, ShieldAlert, Ship, Users, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { NavBar } from '@/components/NavBar';

const getRiskBadge = (score) => {
  if (score >= 85) return { label: 'Critical', color: 'bg-[#8B0000] text-white' };
  if (score >= 65) return { label: 'High', color: 'bg-[#FF3B30] text-white' };
  if (score >= 40) return { label: 'Moderate', color: 'bg-[#FF9500] text-white' };
  return { label: 'Low', color: 'bg-[#2E7D32] text-white' };
};

const getRiskBarColor = (score) => {
  if (score >= 65) return 'bg-[#FF3B30]';
  if (score >= 40) return 'bg-[#FF9500]';
  return 'bg-[#2E7D32]';
};

const SupplierDetailPage = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.getSupplierDetail(supplierId);
        setSupplier(res.data);
      } catch (e) {
        toast.error('Supplier not found');
        navigate('/suppliers');
      } finally { setLoading(false); }
    };
    load();
    api.getSupplierRiskTimeline(supplierId).then(res => setTimeline(res.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const handleDelete = async () => {
    if (!confirm('Delete this supplier?')) return;
    try {
      await api.deleteSupplier(supplierId);
      toast.success('Supplier deleted');
      navigate('/suppliers');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const toggleAlert = () => {
    setAlertEnabled(!alertEnabled);
    toast.success(alertEnabled ? 'Alerts disabled for this supplier' : 'Alerts enabled — you will be notified when this supplier\'s risk score changes by more than 10 points');
  };

  if (loading) return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center text-[#4E6974]">Loading...</div>;
  if (!supplier) return null;

  const risk = getRiskBadge(supplier.risk_score || 0);
  const components = supplier.risk_components || {};

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1200px] mx-auto">
          <Button variant="ghost" onClick={() => navigate('/suppliers')} className="mt-6 text-[#4E6974] hover:text-[#0D3B4F]" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to suppliers
          </Button>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 mb-6">
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#0097A7]/10 flex items-center justify-center">
                      <Factory className="h-7 w-7 text-[#0097A7]" />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-[#0D3B4F]" data-testid="supplier-name">{supplier.supplier_name}</h1>
                      <div className="flex items-center gap-4 mt-1 text-sm text-[#4E6974]">
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {supplier.country_name}{supplier.city_region ? `, ${supplier.city_region}` : ''}</span>
                        {supplier.primary_sector && <span>{supplier.primary_sector}</span>}
                        {supplier.tier && <Badge variant="secondary">{supplier.tier}</Badge>}
                      </div>
                      {supplier.annual_spend_aud > 0 && (
                        <div className="flex items-center gap-1 mt-1 text-sm text-[#0D3B4F]">
                          <DollarSign className="h-3.5 w-3.5" /> Annual spend: ${supplier.annual_spend_aud.toLocaleString()} AUD
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-[#0D3B4F]">{Math.round(supplier.risk_score || 0)}</div>
                      <Badge className={risk.color + ' mt-1'}>{risk.label} risk</Badge>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button variant="outline" size="sm" onClick={toggleAlert} data-testid="alert-toggle">
                        {alertEnabled ? <BellOff className="h-4 w-4 mr-1" /> : <Bell className="h-4 w-4 mr-1" />}
                        {alertEnabled ? 'Alerts on' : 'Enable alerts'}
                      </Button>
                      <Button variant="outline" size="sm" className="text-[#FF3B30] border-[#FF3B30]" onClick={handleDelete} data-testid="delete-supplier-btn">
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Risk Breakdown */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-2">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-[#0D3B4F] mb-4" data-testid="risk-breakdown-title">Risk breakdown</h2>
                  <p className="text-sm text-[#4E6974] mb-5">What's driving this supplier's risk score</p>
                  <div className="space-y-4">
                    {Object.entries(components).map(([key, comp]) => (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-[#0D3B4F]">{comp.label}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-[#0D3B4F]">{comp.score}</span>
                            <span className="text-xs text-[#4E6974]">({comp.weight}% weight)</span>
                          </div>
                        </div>
                        <div className="h-2.5 bg-[#F5F5F5] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${getRiskBarColor(comp.score)} transition-all`} style={{ width: `${comp.score}%` }} />
                        </div>
                        {key === 'country' && (
                          <Link to={`/countries/${supplier.country_code}`} className="text-xs text-[#0097A7] hover:underline mt-0.5 inline-block">
                            View {supplier.country_name} country detail
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* DFAT Sanctions Screening */}
              {supplier.sanctions_screening && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-6">
                  <Card className={`bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 ${supplier.sanctions_screening.status === 'clear' ? 'border-l-[#2E7D32]' : supplier.sanctions_screening.status === 'match' ? 'border-l-[#FF3B30]' : 'border-l-[#FF9500]'}`} data-testid="sanctions-screening-section">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
                          {supplier.sanctions_screening.status === 'clear' ? <ShieldCheck className="h-5 w-5 text-[#2E7D32]" /> : <ShieldAlert className="h-5 w-5 text-[#FF3B30]" />}
                          DFAT Sanctions Screening
                        </h3>
                        <Badge className={supplier.sanctions_screening.status === 'clear' ? 'bg-[#2E7D32]/10 text-[#2E7D32]' : 'bg-[#FF3B30]/10 text-[#FF3B30]'}>
                          {supplier.sanctions_screening.status === 'clear' ? 'CLEAR' : supplier.sanctions_screening.status === 'match' ? 'MATCH' : 'POTENTIAL MATCH'}
                        </Badge>
                      </div>
                      {supplier.sanctions_screening.status === 'clear' ? (
                        <p className="text-sm text-[#4E6974]">No matches found against the DFAT Consolidated Sanctions List. Screened at {supplier.sanctions_screening.screened_at?.substring(0, 10)}.</p>
                      ) : (
                        <div>
                          <p className="text-sm text-[#FF3B30] font-medium mb-2">Sanctions match detected — requires compliance review.</p>
                          {supplier.sanctions_screening.matches?.map((m, i) => (
                            <div key={i} className="p-2 bg-[#FF3B30]/5 rounded text-xs text-[#4E6974] mb-1">
                              <span className="font-medium text-[#0D3B4F]">{m.name}</span>
                              {m.similarity && <span className="ml-2">({Math.round(m.similarity * 100)}% match)</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {supplier.sanctions_screening.country_note && (
                        <p className="text-xs text-[#FF9500] mt-2">{supplier.sanctions_screening.country_note}</p>
                      )}
                      <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: DFAT Consolidated Sanctions List (Regulation 8)</p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* ILO Labour Market */}
              {supplier.ilo_labour && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="mt-6">
                  <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="ilo-labour-section">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
                          <Users className="h-4 w-4 text-[#0097A7]" /> Labour market — {supplier.country_name}
                        </h3>
                        <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">ILO</Badge>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="p-3 rounded-lg bg-[#F5F5F5]">
                          <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Mfg wage</p>
                          <p className="text-base font-bold text-[#0D3B4F]">${supplier.ilo_labour.manufacturing_wage_aud?.toLocaleString()}</p>
                          <p className="text-[10px] text-[#4E6974]">AUD/month</p>
                        </div>
                        <div className="p-3 rounded-lg bg-[#F5F5F5]">
                          <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Wage growth</p>
                          <p className={`text-base font-bold ${supplier.ilo_labour.wage_growth_pct > 10 ? 'text-[#FF9500]' : 'text-[#0D3B4F]'}`}>{supplier.ilo_labour.wage_growth_pct}%</p>
                          <p className="text-[10px] text-[#4E6974]">annual</p>
                        </div>
                        <div className="p-3 rounded-lg bg-[#F5F5F5]">
                          <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Unemployment</p>
                          <p className="text-base font-bold text-[#0D3B4F]">{supplier.ilo_labour.unemployment_pct}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-[#F5F5F5]">
                          <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Safety</p>
                          <p className={`text-base font-bold capitalize ${supplier.ilo_labour.safety_rating === 'poor' ? 'text-[#FF3B30]' : supplier.ilo_labour.safety_rating === 'excellent' ? 'text-[#2E7D32]' : 'text-[#0D3B4F]'}`}>{supplier.ilo_labour.safety_rating}</p>
                        </div>
                      </div>
                      {supplier.ilo_labour.strike_days > 15 && (
                        <p className="text-xs text-[#FF9500] font-medium mt-3">Elevated industrial action: {supplier.ilo_labour.strike_days} days lost to strikes</p>
                      )}
                      <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: International Labour Organisation (ILOSTAT)</p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* ABS Trade Flow */}
              {supplier.abs_trade && supplier.abs_trade.total_imports_aud > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-6">
                  <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="abs-trade-section">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-[#0097A7]" /> Australia–{supplier.country_name} trade
                        </h3>
                        <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">ABS</Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 rounded-lg bg-[#F5F5F5]">
                          <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">AU Imports</p>
                          <p className="text-lg font-bold text-[#0D3B4F]">${(supplier.abs_trade.total_imports_aud / 1e9).toFixed(1)}B</p>
                        </div>
                        <div className="p-3 rounded-lg bg-[#F5F5F5]">
                          <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">AU Exports</p>
                          <p className="text-lg font-bold text-[#0D3B4F]">${(supplier.abs_trade.total_exports_aud / 1e9).toFixed(1)}B</p>
                        </div>
                        <div className="p-3 rounded-lg bg-[#F5F5F5]">
                          <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Balance</p>
                          <p className={`text-lg font-bold ${supplier.abs_trade.trade_balance_aud >= 0 ? 'text-[#2E7D32]' : 'text-[#FF3B30]'}`}>
                            {supplier.abs_trade.trade_balance_aud >= 0 ? '+' : '-'}${(Math.abs(supplier.abs_trade.trade_balance_aud) / 1e9).toFixed(1)}B
                          </p>
                        </div>
                      </div>
                      {supplier.abs_trade.latest_period && <p className="text-xs text-[#4E6974] mt-3 italic">Latest period: {supplier.abs_trade.latest_period}. Source: Australian Bureau of Statistics</p>}
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Currency Exposure */}
              {supplier.currency_exposure && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-6">
                  <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 border-l-[#0097A7]">
                    <CardContent className="p-6">
                      <h3 className="text-base font-bold text-[#0D3B4F] mb-2">Currency exposure</h3>
                      <p className="text-sm text-[#4E6974]">
                        Based on your annual spend of <strong>${supplier.currency_exposure.annual_spend.toLocaleString()} AUD</strong> with this supplier,
                        the <strong>{Math.abs(supplier.currency_exposure.pct_change_30d)}%</strong> movement in AUD/{supplier.currency_exposure.currency_code} over
                        the past 30 days represents an approximate <strong className={supplier.currency_exposure.direction === 'increase' ? 'text-[#FF3B30]' : 'text-[#2E7D32]'}>
                        ${supplier.currency_exposure.cost_impact.toLocaleString()} cost {supplier.currency_exposure.direction}</strong>.
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Risk timeline */}
              {timeline?.months?.length > 1 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mt-6">
                  <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="supplier-risk-timeline">
                    <CardContent className="p-6">
                      <h3 className="text-base font-bold text-[#0D3B4F] mb-4">Risk timeline ({timeline.months.length} months)</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={timeline.months} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" />
                          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#4E6974' }} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#4E6974' }} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="supplier_score" name="Supplier risk" stroke="#0097A7" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="country_score" name="Country risk" stroke="#FF9500" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                      {timeline.drivers?.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-semibold text-[#0D3B4F] uppercase tracking-wide">What moved the score</p>
                          {timeline.drivers.map(d => (
                            <div key={d.month} className="flex items-start gap-2 text-sm text-[#4E6974]" data-testid="timeline-driver">
                              <span className={`font-semibold flex-shrink-0 ${d.delta > 0 ? 'text-[#FF3B30]' : 'text-[#2E7D32]'}`}>
                                {d.delta > 0 ? '+' : ''}{d.delta}
                              </span>
                              <span>{d.month}: {d.reason}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Recent Signals */}
              {supplier.signals?.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-6">
                  <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                    <CardContent className="p-6">
                      <h3 className="text-base font-bold text-[#0D3B4F] mb-4">Recent signals</h3>
                      <div className="space-y-3">
                        {supplier.signals.slice(0, 10).map((sig, i) => (
                          <div key={`${sig.title}-${sig.published_at || i}`} className="flex items-start gap-3 p-3 bg-[#F5F5F5] rounded-lg">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-[#0D3B4F]">{sig.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-[#4E6974]">{sig.published_at?.substring(0, 10)}</span>
                                <Badge variant="secondary" className="text-xs">{sig.risk_category || sig.source}</Badge>
                              </div>
                            </div>
                            {sig.url && (
                              <a href={sig.url} target="_blank" rel="noopener noreferrer" className="text-[#0097A7]">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="space-y-6">
              {/* Alternative Sourcing */}
              {supplier.alternatives?.length > 0 && (
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                  <CardContent className="p-6">
                    <h3 className="text-base font-bold text-[#0D3B4F] mb-2">Alternative sourcing</h3>
                    <p className="text-xs text-[#4E6974] mb-4">
                      If {supplier.country_name} risk remains elevated, consider these alternative markets
                      {supplier.commodities ? ` for ${supplier.commodities.split(',')[0].trim()}` : ''}:
                    </p>
                    <div className="space-y-3">
                      {supplier.alternatives.map(alt => {
                        const altRisk = getRiskBadge(alt.risk_score);
                        return (
                          <Link key={alt.country_code} to={`/countries/${alt.country_code}`} className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-lg hover:bg-[#E0E0E0] transition-colors">
                            <span className="text-sm font-medium text-[#0D3B4F]">{alt.country_name}</span>
                            <Badge className={altRisk.color + ' text-xs'}>{Math.round(alt.risk_score)}</Badge>
                          </Link>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* OEC Alternative Sourcing (product-specific) */}
              {supplier.oec_alternatives?.length > 0 && (
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="oec-alternatives-section">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-bold text-[#0D3B4F]">Product-level alternatives</h3>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">OEC</Badge>
                    </div>
                    <p className="text-xs text-[#4E6974] mb-3">
                      Countries that also export HS {supplier.hs_code}:
                    </p>
                    <div className="space-y-2">
                      {supplier.oec_alternatives.slice(0, 5).map((alt, i) => {
                        const altRisk = getRiskBadge(alt.risk_score || 50);
                        return (
                          <Link key={alt.country_code || i} to={`/countries/${alt.country_code}`} className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-lg hover:bg-[#E0E0E0] transition-colors">
                            <div>
                              <span className="text-sm font-medium text-[#0D3B4F]">{alt.country_name}</span>
                              {alt.export_value > 0 && <span className="text-xs text-[#4E6974] ml-2">${(alt.export_value / 1e6).toFixed(0)}M</span>}
                            </div>
                            <Badge className={altRisk.color + ' text-xs'}>{Math.round(alt.risk_score || 50)}</Badge>
                          </Link>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: Observatory of Economic Complexity</p>
                  </CardContent>
                </Card>
              )}

              {/* Port Status */}
              {supplier.port_status?.length > 0 && (
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="port-status-section">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
                        <Ship className="h-4 w-4 text-[#0097A7]" /> Shipping ports
                      </h3>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">Ports</Badge>
                    </div>
                    <div className="space-y-3">
                      {supplier.port_status.map((port) => {
                        const lc = port.congestion_level === 'Severe' ? '#FF3B30' : port.congestion_level === 'High' ? '#FF9500' : port.congestion_level === 'Moderate' ? '#FFCC02' : '#2E7D32';
                        return (
                          <div key={port.port_id} className="p-3 rounded-lg bg-[#F5F5F5]">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: lc }} />
                                <span className="text-sm font-semibold text-[#0D3B4F]">{port.port_name}</span>
                              </div>
                              <Badge className="text-[10px]" style={{ backgroundColor: `${lc}20`, color: lc }}>{port.congestion_level}</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-1 text-xs text-[#4E6974] mt-1">
                              <span>Wait: {port.wait_days}d</span>
                              <span>Transit to AU: {port.transit_days_to_au}d</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: World Bank CPPI + freight model</p>
                  </CardContent>
                </Card>
              )}

              {/* Active Disasters */}
              {supplier.active_disasters?.length > 0 && (
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 border-l-[#FF9500]">
                  <CardContent className="p-6">
                    <h3 className="text-base font-bold text-[#0D3B4F] mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-[#FF9500]" /> Active hazards
                    </h3>
                    <div className="space-y-2">
                      {supplier.active_disasters.slice(0, 3).map((d, i) => (
                        <div key={d.name || i} className="p-2 bg-[#FF9500]/5 rounded text-xs text-[#4E6974]">
                          <span className="font-medium text-[#0D3B4F]">{d.name?.substring(0, 60)}</span>
                          <Badge className="ml-2 text-[10px]" variant="secondary">{d.severity || d.alert_level}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Supplier Info */}
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-6">
                  <h3 className="text-base font-bold text-[#0D3B4F] mb-3">Details</h3>
                  <div className="space-y-2 text-sm">
                    {supplier.commodities && <div><span className="text-[#4E6974]">Products:</span> <span className="text-[#0D3B4F]">{supplier.commodities}</span></div>}
                    {supplier.hs_code && <div><span className="text-[#4E6974]">HS code:</span> <span className="text-[#0D3B4F]">{supplier.hs_code}</span></div>}
                    {supplier.contact_name && <div><span className="text-[#4E6974]">Contact:</span> <span className="text-[#0D3B4F]">{supplier.contact_name}</span></div>}
                    {supplier.contact_email && <div><span className="text-[#4E6974]">Email:</span> <a href={`mailto:${supplier.contact_email}`} className="text-[#0097A7]">{supplier.contact_email}</a></div>}
                    {supplier.notes && <div><span className="text-[#4E6974]">Notes:</span> <span className="text-[#0D3B4F]">{supplier.notes}</span></div>}
                    <div className="pt-2 border-t border-[#F5F5F5] text-xs text-[#4E6974]">
                      Added: {supplier.created_at?.substring(0, 10)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupplierDetailPage;
